import { useEffect, type MutableRefObject, type ReactNode } from "react";
import { useSwipeDismiss } from "@/hooks/use-swipe-dismiss";
import { cn } from "@/lib/utils";

interface SwipeCardProps {
  label: string;
  /** Kalles når sveipet er ferdig. */
  onDismiss: () => void;
  disabled?: boolean;
  /** Lar køen starte sveipet fra tastaturet. */
  dismissRef?: MutableRefObject<(() => void) | null>;
  /** `leaving` er sann mens kortet sveipes ut; `dismiss` starter sveipet (Avvis-knappen). */
  children: (swipe: { leaving: boolean; dismiss: () => void }) => ReactNode;
}

/**
 * Kortet i køen med Tinder-sveip (VIB-2958, VIB-2986): dra til venstre eller
 * trykk Avvis, så sveipes kortet ut med et rødt «AVVIS». Brukes av signal- og
 * oppfølgingskortet.
 */
export default function SwipeCard({ label, onDismiss, disabled = false, dismissRef, children }: SwipeCardProps) {
  const swipe = useSwipeDismiss({ onDismiss, disabled });
  useEffect(() => {
    if (!dismissRef) return;
    dismissRef.current = swipe.dismiss;
    return () => {
      dismissRef.current = null;
    };
  }, [dismissRef, swipe.dismiss]);

  const transform = swipe.leaving
    ? "translateX(-140%) rotate(-14deg)"
    : swipe.dx
      ? `translateX(${swipe.dx}px) rotate(${swipe.dx / 28}deg)`
      : undefined;

  return (
    <article
      aria-label={label}
      {...swipe.handlers}
      style={{ transform, opacity: swipe.leaving ? 0 : 1, touchAction: "pan-y" }}
      className={cn(
        "relative space-y-5 overflow-hidden border-[1.5px] border-foreground bg-card p-6 select-none motion-reduce:transition-none",
        swipe.dragging ? "cursor-grabbing" : "cursor-grab transition-[transform,opacity] duration-[420ms] ease-in",
      )}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-destructive" style={{ opacity: swipe.progress * 0.1 }} />
      <div
        aria-hidden
        className="pointer-events-none absolute right-7 top-8 z-10 rotate-[14deg] rounded-md border-4 border-destructive bg-card/85 px-4 font-display text-3xl font-semibold tracking-[0.1em] text-destructive"
        style={{ opacity: swipe.progress }}
      >
        AVVIS
      </div>
      {children({ leaving: swipe.leaving, dismiss: swipe.dismiss })}
    </article>
  );
}
